import { createSelector } from 'reselect'

import { DATE_VALUES, getParsedDateUntilToday } from 'utils/dateUtils'

import { PLAN_TYPES } from 'containers/App/constants'
import { selectActualLicenses } from 'containers/App/selectors'

import { STARTER_DATA } from './constants'

// `dashboard` is injected only while a Dashboard route is mounted and dropped
// again once another route takes over; in-flight subscriptions/epics can
// still read state in that window, so slice access must be null-safe (see
// AwarenessCenter/Campaigns/selectors.js precedent). This is the confirmed
// crash site: selectDateFilter threw `Cannot read properties of undefined
// (reading 'threatProtection')` when an epic read state after the slice was
// dropped.
const EMPTY_OBJ = {}
const EMPTY_SUB_WIDGET = { data: EMPTY_OBJ, loader: true, error: null }
// Mirrors the reducer's initial `data` shape so consumers see the same
// keys whether the slice is mounted or not.
const EMPTY_DATA = { analystResolution: EMPTY_OBJ }
const selectFlow = (state) => state.dashboard?.threatProtection?.flow ?? EMPTY_OBJ

const selectPlanIsStarter = (state) => selectActualLicenses(state).plan === PLAN_TYPES.STARTER
const selectFlowData = (state) => selectFlow(state).data ?? EMPTY_DATA
const selectData = createSelector(
  selectPlanIsStarter,
  selectFlowData,
  (planIsStarter, flowData) => {
    if (!planIsStarter) return flowData
    return {
      ...flowData,
      ...STARTER_DATA,
    }
  }
)
const selectLoader = (state) => selectFlow(state).loader ?? false
const selectError = (state) => selectFlow(state).error ?? null

const selectMostTargetedEmployeesData = (state) =>
  (selectFlow(state).mostTargetedEmployees ?? EMPTY_SUB_WIDGET).data ?? EMPTY_OBJ
const selectMostTargetedEmployeesDataSecondPart = (state) =>
  (selectFlow(state).mostTargetedEmployees ?? EMPTY_SUB_WIDGET).dataSecondPart ?? EMPTY_OBJ
const selectMostTargetedEmployeesLoader = (state) =>
  (selectFlow(state).mostTargetedEmployees ?? EMPTY_SUB_WIDGET).loader ?? true
const selectMostTargetedEmployeesError = (state) =>
  (selectFlow(state).mostTargetedEmployees ?? EMPTY_SUB_WIDGET).error ?? null

const selectMitigationData = (state) =>
  (selectFlow(state).mitigation ?? EMPTY_SUB_WIDGET).data ?? EMPTY_OBJ
const selectMitigationLoader = (state) =>
  (selectFlow(state).mitigation ?? EMPTY_SUB_WIDGET).loader ?? true
const selectMitigationError = (state) =>
  (selectFlow(state).mitigation ?? EMPTY_SUB_WIDGET).error ?? null

const selectMailboxAnomaly = (state) => {
  const planIsStarter = selectActualLicenses(state).plan === PLAN_TYPES.STARTER
  return planIsStarter
    ? STARTER_DATA.mailboxAnomaly
    : ((selectFlow(state).mailboxAnomaly ?? EMPTY_SUB_WIDGET).data ?? EMPTY_OBJ)
}
const selectMailboxAnomalyLoader = (state) =>
  (selectFlow(state).mailboxAnomaly ?? EMPTY_SUB_WIDGET).loader ?? true
const selectMailboxAnomalyError = (state) =>
  (selectFlow(state).mailboxAnomaly ?? EMPTY_SUB_WIDGET).error ?? null

const selectTopProfiles = (state) =>
  (selectFlow(state).topProfiles ?? EMPTY_SUB_WIDGET).data ?? EMPTY_OBJ
const selectTopProfilesLoader = (state) =>
  (selectFlow(state).topProfiles ?? EMPTY_SUB_WIDGET).loader ?? true
const selectTopProfilesError = (state) =>
  (selectFlow(state).topProfiles ?? EMPTY_SUB_WIDGET).error ?? null

const selectDateFilter = (state) => selectFlow(state).dateFilter ?? DATE_VALUES.MONTH
const selectParsedDate = (state) => {
  const currentDateFilter = selectDateFilter(state)
  return getParsedDateUntilToday(currentDateFilter)
}

const selectAllFilters = (state) => selectFlow(state).allFilters ?? EMPTY_OBJ
const selectAllFiltersLoader = (state) => selectFlow(state).allFiltersLoader ?? false

export {
  selectData,
  selectLoader,
  selectError,
  selectMostTargetedEmployeesData,
  selectMostTargetedEmployeesDataSecondPart,
  selectMostTargetedEmployeesLoader,
  selectMostTargetedEmployeesError,
  selectMitigationData,
  selectMitigationLoader,
  selectMitigationError,
  selectMailboxAnomaly,
  selectMailboxAnomalyLoader,
  selectMailboxAnomalyError,
  selectTopProfiles,
  selectTopProfilesLoader,
  selectTopProfilesError,
  selectDateFilter,
  selectAllFilters,
  selectAllFiltersLoader,
  selectParsedDate,
}
